import { FC } from 'react';
import { Divider } from '../../../components/divider/divider';
import { InlineInputs } from '../../../components/inline-inputs/inline-inputs';
import { Switch } from '../../../components/switch/switch';
import { getWeekDays } from '../../../helpers/date-time';
import { useLang } from '../../../hooks/use-lang';
import { TimePicker } from '../../date-time/time-picker/time-picker';
import { WorkingHoursType } from '../types';
import s from './working-hours-week.module.css';

interface Props {
  handleUpdateWeek: (week: WorkingHoursType['week']) => void;
  week?: WorkingHoursType['week'];
}

const offDay = '00:00-00:00';
const partDay = '09:00-20:00';
const allDay = '00:00-23:59';

export const WorkingHoursWeek: FC<Props> = ({ handleUpdateWeek, week }) => {
  const { t } = useLang();
  const updatedWeeks = [...(week || [])];

  const handleSetWeeklyFrom = (from: string, index: number) => {
    let updatedTime = updatedWeeks[index] || offDay;
    const [, to] = updatedTime.split('-');
    updatedTime = `${from}-${to}`;
    updatedWeeks[index] = updatedTime;
    handleUpdateWeek(updatedWeeks);
  };

  const handleSetWeeklyTo = (to: string, index: number) => {
    let updatedTime = updatedWeeks[index] || offDay;
    const [from] = updatedTime.split('-');
    updatedTime = `${from}-${to}`;
    updatedWeeks[index] = updatedTime;
    handleUpdateWeek(updatedWeeks);
  };

  const handleDayToggle = (index: number, isOn: boolean) => {
    if (updatedWeeks.length === 0)
      Array.from({ length: 7 }).forEach(() => updatedWeeks.push(offDay));
    updatedWeeks[index] = isOn ? allDay : offDay;
    handleUpdateWeek(updatedWeeks);
  };

  const isAllDaysEnabled = updatedWeeks.every((day) => day === allDay);

  const toggleAllDay = () => {
    if (isAllDaysEnabled) {
      handleUpdateWeek?.([...Array.from({ length: 5 }, () => partDay)]);
    } else {
      handleUpdateWeek?.(Array.from({ length: 7 }, () => allDay));
    }
  };

  return (
    <div className={s.feature}>
      <label className={s.label}>{t('Часы работы')}</label>
      <ul className={s.list}>
        {getWeekDays('dddd').map((day, index) => {
          const range = updatedWeeks[index];
          const isOn = !!range && range !== offDay;
          const time = range?.split('-') || [];

          return (
            <li key={index} className={s.item}>
              <span className={s.day}>{day}</span>
              <Switch
                isOn={isOn}
                onToggle={(isOn) => handleDayToggle(index, isOn)}
                onLabel={t('Включено')}
                offLabel={t('Отключено')}
                data-testid={`week-day-${index}`}
              />
              {isOn && (
                <InlineInputs>
                  <TimePicker
                    value={time[0]}
                    onChange={(e) => handleSetWeeklyFrom(e, index)}
                    required
                    name={`from-${index}`}
                    placeholder={t('От')}
                    data-testid={`week-from-${index}`}
                  />
                  <TimePicker
                    value={time[1]}
                    onChange={(e) => handleSetWeeklyTo(e, index)}
                    required
                    name={`to-${index}`}
                    placeholder={t('До')}
                    data-testid={`week-to-${index}`}
                  />
                </InlineInputs>
              )}
            </li>
          );
        })}
      </ul>
      <Divider />
      <div className={s.note}>
        <div className={s.description}>
          <h3>{t('Круглосуточно')}</h3>
          <h4>{t('Меню будет работать с понедельника по воскресенье')}</h4>
        </div>
        <Switch isOn={isAllDaysEnabled} onToggle={toggleAllDay} data-testid="all-days-switch" />
      </div>
    </div>
  );
};
